import { decodeJwt } from 'jose';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import { Loader } from '../components/shared/Loading';
import * as constants from '../constants/common';
import instances from './axios';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  parseSearchParamsRaw,
  getLocationPathname,
  getLocationSearch,
  locationReload,
  locationReplace,
  getLocationOrigin,
} from '../helpers/common';
import { intersection } from 'lodash';
import { havePermission } from '../helpers/auth';
import {
  saveRedirectUrl,
  getRedirectUrl as getSavedRedirectUrl,
  clearRedirectUrl,
} from '../helpers/redirectHelper';
import { logoutUser } from '../services/apis';

export const AuthContext = createContext();

// Create a BroadcastChannel for cross-tab communication
const LOGOUT_CHANNEL_NAME = 'auth_logout_channel';
let logoutChannel;
try {
  // eslint-disable-next-line no-undef
  logoutChannel = new BroadcastChannel(LOGOUT_CHANNEL_NAME);
} catch (e) {
  // BroadcastChannel not supported, will use localStorage fallback
  logoutChannel = null;
}

const AuthenticationProvider = ({ children }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const [userInfo, setUserInfo] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem(constants.TOKEN_KEY));
  const [refreshToken, setRefreshToken] = useState(
    localStorage.getItem(constants.REFRESH_TOKEN_KEY),
  );
  const [roles, setRoles] = useState(null);
  const [loading, setLoading] = useState(false);

  const urlParams = parseSearchParamsRaw(getLocationSearch());
  const code = urlParams.authCode;
  const state = urlParams.state;
  const codeChallenge = localStorage.getItem('codeVerify');

  const loadUserInfo = (userInfoP) => {
    try {
      const profile = decodeJwt(userInfoP.accessToken);

      setIsAuthenticated(true);
      localStorage.setItem(constants.TOKEN_KEY, userInfoP.accessToken);
      localStorage.setItem(constants.REFRESH_TOKEN_KEY, userInfoP.refreshToken);
      setToken(userInfoP.accessToken);
      setRefreshToken(userInfoP.refreshToken);

      const tax_number = profile?.VAT;

      setRoles(userInfoP.roles);

      setUserInfo({
        ...profile,
        ...userInfoP,
        tax_number,
        firstName: userInfoP.taxPayerName,
        lastName: '',
      });
    } catch (error) {
      console.error('Failed to load user profile', error);
    }
  };

  const login = (userInfoP) => {
    userInfoP && loadUserInfo(userInfoP);
  };

  const logout = async (options = {}) => {
    // Save redirect URL before clearing localStorage
    if (options.redirect) {
      saveRedirectUrl(options.redirect);
    }

    const response = await logoutUser();

    const redirectUrl = response.data?.logoutLink;

    setIsAuthenticated(false);
    setUserInfo(null);
    setToken(null);
    setRefreshToken(null);
    sessionStorage.clear();
    localStorage.clear();

    if (options.sessionTimeout) {
      localStorage.setItem('sessionExpired', true);
    }

    // Restore redirect URL after clearing localStorage
    if (options.redirect) {
      saveRedirectUrl(options.redirect);
    }

    // Notify other tabs about logout
    if (logoutChannel) {
      logoutChannel.postMessage('logout');
    } else {
      // Fallback to localStorage event
      localStorage.setItem('logout_event', Date.now().toString());
      localStorage.removeItem('logout_event');
    }

    redirectUrl ? locationReplace(redirectUrl) : locationReload();
  };

  const getRedirectUrl = async () => {
    setLoading(true);
    const url = await instances.authInstance.get(constants.AUTH_LOGIN_URL);
    let redirectParams = parseSearchParamsRaw(url.data.redirectUrl);
    let codeVerify = redirectParams.Hashkey;
    localStorage.setItem('codeVerify', codeVerify);
    locationReplace(url.data.redirectUrl);
  };

  const callCallback = async (codeP, stateP, hashKey) => {
    setLoading(true);
    try {
      const callback = await instances.authInstance.post(
        constants.AUTH_CALLBACK_URL,
        {
          code: codeP,
          state: stateP,
          hashKey,
        },
      );
      loadUserInfo(callback.data);
      localStorage.removeItem('codeVerify');
      localStorage.setItem('uinfo', JSON.stringify(callback.data));
      setLoading(false);

      // Check for saved redirect URL
      const redirectUrl = getSavedRedirectUrl();
      if (redirectUrl) {
        clearRedirectUrl();
        locationReplace(getLocationOrigin() + redirectUrl);
      } else {
        locationReplace(getLocationOrigin());
      }
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      !intersection(
        getLocationPathname().split('/'),
        constants.WHITELISTED_ROUTES,
      ).length
    ) {
      if (!token) {
        if (code && state) {
          getLocationPathname().includes(i18n.language) &&
            callCallback(code, state, codeChallenge);
        } else {
          navigate(`/${i18n.language}`);
        }
      } else {
        loadUserInfo(JSON.parse(localStorage.getItem('uinfo')));
        setLoading(false);
      }
    }
  }, []);

  // Listen for logout events from other tabs
  useEffect(() => {
    const handleLogoutFromOtherTab = (event) => {
      if (logoutChannel && event) {
        // Using BroadcastChannel
        if (event.data === 'logout') {
          locationReload();
        }
      }
    };

    const handleStorageChange = (event) => {
      // Fallback using storage event
      if (event.key === 'logout_event') {
        locationReload();
      }
    };

    if (logoutChannel) {
      logoutChannel.addEventListener('message', handleLogoutFromOtherTab);
    }

    // Always listen to storage events as fallback
    window.addEventListener('storage', handleStorageChange);

    return () => {
      if (logoutChannel) {
        logoutChannel.removeEventListener('message', handleLogoutFromOtherTab);
      }
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const hasRole = (role) => {
    return havePermission(roles, role);
  };

  const value = useMemo(
    () => ({
      isAuthenticated,
      token,
      refreshToken,
      logout,
      userInfo,
      login,
      roles,
      getRedirectUrl,
      hasRole,
    }),
    [isAuthenticated, token, refreshToken, userInfo, roles],
  );

  if (loading) {
    return <Loader active={true} />;
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthenticationProvider;
